/**
 * Premium content catalog — the subscription-gated, platform-curated library.
 * List/favorites require an active premium subscription OR the
 * `premium_content_manage` permission; the item PUT is curator-only.
 */
import type {
  ApiPremiumCatalogItem,
  FetchPremiumCatalogArgs,
  SetPremiumItemArgs,
} from './types/premiumContent.types';

import { destroy, post, postWithPagination, put } from './axios';

// A trainer's catalog access lives in the organization they work in, which
// the backend reads only from the path.
const premiumBase = (organizationId?: string) =>
  organizationId
    ? `/v2/organizations/${organizationId}/premium-content`
    : '/v2/premium-content';

export const fetchPremiumCatalog = async ({
  contentId,
  contentType,
  favoritesOnly,
  organizationId,
  search,
  tableFilters,
}: FetchPremiumCatalogArgs) => {
  const params = {
    ...(contentType?.length ? { contentType } : {}),
    ...(contentId ? { contentId } : {}),
    ...(favoritesOnly ? { favoritesOnly: true } : {}),
    ...(search ? { search } : {}),
    offset: Number(tableFilters.offset ?? '1'),
    limit: Number(tableFilters.limit ?? '25'),
  };
  const response = await postWithPagination<ApiPremiumCatalogItem[]>(
    `${premiumBase(organizationId)}/list`,
    params,
  );
  if (!response?.data) return null;
  return response;
};

// resolve to the favorite state the BE confirms (POST ⇒ favorited,
// DELETE ⇒ not favorited; both endpoints are idempotent). Throws on failure
// (the axios helpers re-throw known error statuses).
export const favoritePremiumItem = async (
  premiumContentId: string,
  organizationId?: string,
) => {
  await post(`${premiumBase(organizationId)}/${premiumContentId}/favorite`);
  return true;
};

export const unfavoritePremiumItem = async (
  premiumContentId: string,
  organizationId?: string,
) => {
  await destroy(`${premiumBase(organizationId)}/${premiumContentId}/favorite`);
  return false;
};

export const setPremiumItem = async ({
  contentId,
  contentType,
  enabled,
}: SetPremiumItemArgs) => {
  const response = await put<{
    data: { premiumContentId: string | null; isPremium: boolean };
  }>(`/v2/premium-content/items/${contentType}/${contentId}`, { enabled });
  return response?.data ?? null;
};
